import { Component, Injector, OnDestroy, OnInit, QueryList, ViewChildren, AfterViewInit } from '@angular/core';
import TruvBridge, { SuccessMetadata } from '@truv/bridge';
import { firstValueFrom, forkJoin, Observable, Observer, Subscription } from 'rxjs';
import { Borrower } from '../../../models/borrower-model';
import { BorrowerVerificationUserEnrollmentResponse } from '../../../models/borrower-verification-user-enrollment.model';
import { Employment } from '../../../models/employment-model';
import { TypeOfIncome } from '../../../models/enums/type-of-income.enum';
import { Income } from '../../../models/income-model';
import { IncomeService } from '../../../services/income-service';
import { BorrowerDigitalIncomeComponent } from '../borrower-digital-income/borrower-digital-income.component';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { DigitalIncomeVerificationStep } from '../../../models/wizard/digital-income-verification-step.model';
import { BorrowerVerificationService } from '../../../services/borrower-verification.service';
import { Timer, TimerService } from '../../../services/timer.service';
import { BorrowerVerificationOrderStatus } from '../../../models/borrower-verification-order-history.model';
import { SignalRService } from '../../../services/signalr.service';
import { ArgyleService } from '../../../services/argyle.service';

@Component({
    selector: 'digital-income-verification-step',
    templateUrl: 'digital-income-verification-step.component.html',
    styleUrls: ['digital-income-verification-step.component.scss'],
    standalone: false
})
export class DigitalIncomeVerificationStepComponent
  extends WizardStepComponentBase<DigitalIncomeVerificationStep>
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChildren('borrowerDigitalIncome') borrowerIncomeComponents:
    | QueryList<BorrowerDigitalIncomeComponent>
    | undefined;

  public allFieldsFilled: boolean = false;

  mainBorrower!: Borrower;
  coBorrowers: Borrower[] = [];

  voeEnabled: boolean = true;

  thirdPartyEnrollmentWidget: string;

  verificationErrorMessage: string = '';

  private _truvBridge: any;
  private _argyleLink: any;

  private _linkCreated: boolean = false;

  /**
   * True when thirdPartyEnrollmentWidget is an order token. Bridge then runs in order mode and reports aggregate
   * completion itself instead of the step inferring it from a single connection plus a close.
   */
  private _usesOrdersApi: boolean = false;

  private _doneProcessing: boolean = false;

  /**
   * Latched for the duration of a completion attempt. Truv can report the same order as COMPLETED more than
   * once, so without this a duplicate would run the completion twice.
   */
  private _completionLatched: boolean = false;

  /** Guards the step against being advanced twice - once on completion and again on a trailing Bridge close. */
  private _advancedToNextStep: boolean = false;

  private _componentIsBeingDestroyed: boolean = false;

  private _loanDocTaskId: number | null | undefined;
  private _borrowerVerificationOrderId: number | null | undefined;
  private _processEventsTimer: Timer | undefined | null;

  private _timerTickedSubscription: Subscription;
  private _timerExpiredSubscription: Subscription;
  private _signalRVerificationSub?: Subscription;

  constructor(
    private readonly _injector: Injector,
    private readonly _incomeService: IncomeService,
    private readonly _borrowerVerificationService: BorrowerVerificationService,
    private readonly _timerService: TimerService,
    private readonly _signalRService: SignalRService,
    private readonly _argyleService: ArgyleService
  ) {
    super(_injector);
    if (this.currentBorrower) {
      this.mainBorrower = this.currentBorrower;
      this.coBorrowers = this.mortgageApplication.borrowers.filter(
        (b) =>
          b.primaryEmail.toLocaleLowerCase() !==
          this.mainBorrower.primaryEmail.toLocaleLowerCase()
      );
    }
    this.saveMortgageApplicationBeforeNextStep = false;
    if (!this.isEditEnabled) {
      this.voeEnabled = this.wizardFlowService.context.config.voeEnabled;
    }
  }

  onSpecifyIncomeAndEmploymentClicked = () => {
    this.wizardFlowService.navigateToManualEmploymentAndIncomeStep();
  };

  getBorrowersForDigitalIncomeCheck(): Borrower[] {
    const borrowers: Borrower[] = [];
    borrowers.push(this.mainBorrower);
    if (this.inCoApplyFlow) {
      return borrowers;
    }
    this.coBorrowers.forEach((borrower) => {
      if (this.borrowerSetting(borrower)) {
        borrowers.push(borrower);
      }
    });
    return borrowers;
  }

  private allBorrowersDigitalIncomeChecked(): boolean {
    const borrowers = this.getBorrowersForDigitalIncomeCheck();
    for (let i = 0; i < borrowers.length; i++) {
      if (!borrowers[i].permissionToRunIncome) {
        return false;
      }
    }
    return true;
  }

  private onCheckClickedOnBorrowerCredit = (e: any): void => {
    this.allFieldsFilled = this.allBorrowersDigitalIncomeChecked();
    this.setNextButtonText();
  };

  private setNextButtonText = () => {
    if (this.allFieldsFilled) {
      this.nextButtonText = this.translocoService.translate('common.continue');
    } else {
      this.nextButtonText = this.translocoService.translate('wizard.digitalIncome.continueWithoutDigitalVerification');
    }
    if (this.step.vendorToUse === 'Truv' || this.step.vendorToUse === 'Argyle') {
      this.nextButtonText = this.translocoService.translate('common.skip');
    }
  };

  private extractIncomeAndEmploymentData(
    borrower: Borrower,
    incomeAndEmploymentData: any
  ) {
    const transactions: any[] = incomeAndEmploymentData.transactions;
    for (const transaction of transactions) {
      const digitallySourcedEmployment =
        this.extractEmploymentFromTransaction(transaction);
      this.extractEmploymentFromDigitallySourcedEmployment(
        borrower,
        digitallySourcedEmployment
      );
    }
  }

  private extractEmploymentFromTransaction(transaction: any): any {
    const employerCode = transaction.employer.employerCode;
    const employerName = transaction.employer.name;
    const years = Math.floor(transaction.employee.monthsInService / 12);
    const months = transaction.employee.monthsInService % 12;
    const position = transaction.employee.employmentPositionTitle;
    const incomes = transaction.annualCompensation;
    const baseComp = transaction.baseCompensation;

    const digitallySourcedEmployment = {
      employerWithStatus: `${employerName} Employer (${
        [
          1, 2, 3, 5, 6, 9, 10, 11, 13, 14, 17, 18, 19, 42, 43, 52, 53, 54, 58,
        ].includes(Number(transaction.employee.status.code))
          ? 'Active'
          : 'Inactive'
      })`,
      originalHireDate: transaction.employee.originalHireDate,
      terminationDate: transaction.employee.terminationDate,
      address: transaction.employer.address,
      employmentStatus: [
        1, 2, 3, 5, 6, 9, 10, 11, 13, 14, 17, 18, 19, 42, 43, 52, 53, 54, 58,
      ].includes(Number(transaction.employee.status.code))
        ? 'Active'
        : 'Inactive',
      position,
      employerCode,
      employerName,
      years,
      months,
      incomes,
      baseComp,
    };
    return digitallySourcedEmployment;
  }

  private extractEmploymentFromDigitallySourcedEmployment(
    borrower: Borrower,
    digitallySourcedEmployment: any
  ) {
    const employment = new Employment();
    // employment.employmentId = -1 * (new Date()).valueOf();
    employment.borrowerId = borrower.borrowerId;
    employment.addressId = -1 * new Date().valueOf();
    employment.employer = digitallySourcedEmployment.employerName;
    employment.employmentType =
      digitallySourcedEmployment.employmentStatus === 'Active'
        ? 'CurrentEmployer'
        : 'FormerEmployer';
    employment.position = digitallySourcedEmployment.position;
    employment.startDate = digitallySourcedEmployment.originalHireDate;
    employment.endDate = digitallySourcedEmployment.terminationDate;
    employment.yearsOnJob = digitallySourcedEmployment.years;
    employment.monthsOnJob = digitallySourcedEmployment.months;
    employment.isDigitallyVerified = true;
    employment.theWorkNumberEmployerCode =
      digitallySourcedEmployment.employerCode;
    employment.address = digitallySourcedEmployment.address;

    const incomes: any[] = digitallySourcedEmployment.incomes.sort(
      (a: any, b: any) => (a.year > b.year ? -1 : 1)
    );
    for (const income of incomes) {
      if (income.base > 0) {
        const incomeObj: Income = new Income();
        incomeObj.borrowerId = employment.borrowerId;
        //incomeObj.companyId = employment.companyId;
        incomeObj.monthlyIncome = income.base / 12;
        incomeObj.typeOfIncome = TypeOfIncome.Base;
        employment.incomes.push(incomeObj);
        //check to see if this employment is already in the array
        const index = borrower.employments.findIndex(
          (e) =>
            e.theWorkNumberEmployerCode ===
              employment.theWorkNumberEmployerCode &&
            e.position === employment.position &&
            e.monthlyIncome === employment.monthlyIncome
        );
        if (index === -1) {
          borrower.employments.push(employment);
        }
        //There should be only one base salary, just return. For some reason, there were multiple base
        //salaries coming from server.
        return;
      }
      /* We are only interested in base income
      if (income.bonus > 0) {
        let incomeObj: Income = new Income();
        incomeObj.borrowerId = employment.borrowerId;
        incomeObj.categoryOfIncome = CategoryOfIncome.Employment;
        incomeObj.monthlyIncome = income.bonus / 12;
        incomeObj.typeOfIncome = "3";
        employment.incomes.push(incomeObj);
      }
      if (income.commission > 0) {
        let incomeObj: Income = new Income();
        incomeObj.borrowerId = employment.borrowerId;
        incomeObj.categoryOfIncome = CategoryOfIncome.Employment;
        incomeObj.monthlyIncome = income.comission / 12;
        incomeObj.typeOfIncome = "4";
        employment.incomes.push(incomeObj);
      }
      if (income.other > 0) {
        let incomeObj: Income = new Income();
        incomeObj.borrowerId = employment.borrowerId;
        incomeObj.categoryOfIncome = CategoryOfIncome.Employment;
        incomeObj.monthlyIncome = income.other / 12;
        incomeObj.typeOfIncome = "9";
        employment.incomes.push(incomeObj);
      }
      if (income.overtime > 0) {
        let incomeObj: Income = new Income();
        incomeObj.borrowerId = employment.borrowerId;
        incomeObj.categoryOfIncome = CategoryOfIncome.Employment;
        incomeObj.monthlyIncome = income.overtime / 12;
        incomeObj.typeOfIncome = "10";
        employment.incomes.push(incomeObj);
      }
      */
    }
  }

  private initializeTruvBridge = () => {
    setTimeout(() => {
      this._truvBridge = TruvBridge.init({
        bridgeToken: this.thirdPartyEnrollmentWidget,
        isOrder: this._usesOrdersApi,
        position: {
          type: 'inline',
          container: document.querySelector('#digital-incomes-order-container'),
        },
        onLoad: () => {
          // Optional, called when Bridge loads
          console.log('Bridge loaded');
          const iframe = document.getElementById('citadel-widget-flow');
          const bridge = iframe.getElementsByClassName('truv-bridge__background');
        },
        onSuccess: (public_token: string, metadata: SuccessMetadata) => {
          console.log('success handler');
          // Send the public_token to your server to exchange for an access_token
          // and retrieve payroll data.
          // The metadata object contains info about the Link.
          console.log('token: ', public_token);
          console.log('metadata: ', metadata);
        },
        onEvent: async (event_type: string, payload: any, source?: string) => {
          // all events fire this function. event_type indicates what the event is,
          // payload has additional information depending on the event. In order mode
          // Bridge passes the emitting surface ("order" page vs "bridge" widget) as a third argument.
          console.log('event: ', event_type);
          console.log('payload: ', payload);

          if (event_type == 'LINK_CREATED') {
            // In order mode a created link is one employer of the order, not the end of the verification.
            this._linkCreated = true;
          } else if (event_type == 'COMPLETED' && this._usesOrdersApi && source == 'order') {
            await this.completeTruvVerification();
          } else if (event_type == 'CLOSE' && !this._usesOrdersApi) {
            if (this._linkCreated) {
              await this.completeTruvVerification();
            }
          } else if (event_type == 'ERROR') {
            console.error('Bridge error: ', payload);
            if (payload?.error?.error_type == 'SESSION_EXPIRED') {
              this.verificationErrorMessage = this.translocoService.translate('wizard.digitalIncome.unableToInitialize');
              this.cdr.detectChanges();
            }
          }
        },
        onClose: () => {
          // Optional, called when Bridge is closed by the user.
          console.log('Bridge closed');
          if (!this._componentIsBeingDestroyed && this._doneProcessing && !this._advancedToNextStep) {
            this._advancedToNextStep = true;
            super.onNextClicked();
          }
        },
      });

      this._truvBridge.open();
    }, 100);
  };

  private completeTruvVerification = async () => {
    if (this._completionLatched) {
      return;
    }
    this._completionLatched = true;

    await this.markVerificationAsComplete();
    await this.processTruvEvents();
    this._doneProcessing = true;

    if (this._advancedToNextStep) {
      return;
    }
    this._advancedToNextStep = true;
    super.onNextClicked();
  };

  private markVerificationAsComplete = async () => {
    this.startSpinner();
    try {
      await firstValueFrom(
        this._borrowerVerificationService.markBorrowerVerificationTaskComplete(
          this._loanDocTaskId
        )
      );
    } catch (error) {
      // Marking the task complete is best-effort; the webhook reconciles it server side.
    } finally {
      this.stopSpinner();
    }
  };

  private processTruvEvents = (): Promise<void> => {
    const timeoutMs = 60000; // 1 minute timeout fallback

    return new Promise<void>((resolve) => {
      this.startSpinner();

      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        this._signalRVerificationSub?.unsubscribe();
        this.stopSpinner();
        this.cdr.detectChanges();
        resolve();
      };

      // Listen for the SignalR push event
      this._signalRVerificationSub = this._signalRService.borrowerVerificationStatusUpdated$
        .subscribe(payload => {
          if (payload.borrowerVerificationOrderId !== this._borrowerVerificationOrderId) {
            return;
          }
          console.log('BorrowerVerificationStatusUpdated received via SignalR:', payload.status);

          // Call processEvents once to let the backend finalize
          this._borrowerVerificationService.processEvents(this._borrowerVerificationOrderId)
            .subscribe({
              next: (result) => {
                if (result && (
                  result.status === BorrowerVerificationOrderStatus.AccountConnected ||
                  result.status === BorrowerVerificationOrderStatus.ReportReady
                )) {
                  console.log('Truv verification completed via SignalR.');
                  finish();
                }
              },
              error: (err) => {
                console.log('Error processing events after SignalR notification:', err);
                finish();
              }
            });
        });

      // Timeout fallback: if no SignalR event arrives within timeoutMs, resolve anyway
      setTimeout(() => {
        if (!resolved) {
          console.log('Truv verification timed out waiting for SignalR event.');
          finish();
        }
      }, timeoutMs);
    });
  };

  private getTokenAndInitializeTruv = () => {
    this.startSpinner();
    const observer: Observer<BorrowerVerificationUserEnrollmentResponse> = {
      next: (response: any) => {
        this.thirdPartyEnrollmentWidget = response.htmlWidgetSrc;
        this._usesOrdersApi = response.usesOrdersApi === true;
        this._loanDocTaskId = response.loanDocTaskId;
        this._borrowerVerificationOrderId =
          response.borrowerVerificationOrderId;
        this.initializeTruvBridge();
      },
      error: (error: any) => {
        console.log(error);
        const serverMessage = error?.error?.message || error?.message || '';
        if (serverMessage.toLowerCase().includes('phone')) {
          this.verificationErrorMessage = this.translocoService.translate('wizard.digitalIncome.phoneNumberInvalid');
        } else {
          this.verificationErrorMessage = this.translocoService.translate('wizard.digitalIncome.unableToInitialize');
        }
      },
      complete: () => {
        console.log('complete');
      },
    };

    this._incomeService
      .initializeBorrowerIncomeAndEmploymentVerification(
        this.mainBorrower?.contactId,
        this.mortgageApplication.applicationId,
        this.step.vendorToUse
      )
      .subscribe(observer)
      .add(() => {
        this.stopSpinner();
        this.cdr.detectChanges();
      });
  };

  private getTokenAndInitializeArgyle = () => {
    this.startSpinner();
    const observer: Observer<BorrowerVerificationUserEnrollmentResponse> = {
      next: (response: any) => {
        this.thirdPartyEnrollmentWidget = response.htmlWidgetSrc;
        this._loanDocTaskId = response.loanDocTaskId;
        this._borrowerVerificationOrderId =
          response.borrowerVerificationOrderId;
        this.initializeArgyleLink();
      },
      error: (error: any) => {
        console.log(error);
      },
      complete: () => {
        console.log('complete');
      },
    };

    this._incomeService
      .initializeBorrowerIncomeAndEmploymentVerification(
        this.mainBorrower?.contactId,
        this.mortgageApplication.applicationId,
        this.step.vendorToUse
      )
      .subscribe(observer)
      .add(() => {
        this.stopSpinner();
        this.cdr.detectChanges();
      });
  };

  private initializeArgyleLink = async () => {
    try {
      const Argyle = await this._argyleService.loadArgyleSdk();
      this._argyleLink = Argyle.create({
        userToken: this._argyleService.extractUserToken(this.thirdPartyEnrollmentWidget),
        sandbox: this._argyleService.isSandbox(),
        onAccountConnected: async ({ accountId, userId }: { accountId: string; userId: string }) => {
          console.log('Argyle account connected:', accountId);
          this._linkCreated = true;
        },
        onAccountError: async ({ accountId, userId }: { accountId: string; userId: string }) => {
          console.log('Argyle account error:', accountId);
        },
        onClose: async () => {
          console.log('Argyle Link closed');
          if (!this._componentIsBeingDestroyed) {
            if (this._linkCreated) {
              await this.markVerificationAsComplete();
              this._doneProcessing = true;
              super.onNextClicked();
            }
          }
        },
        onTokenExpired: async (updateToken: (token: string) => void) => {
          console.log('Argyle token expired, refreshing...');
          try {
            const response = await firstValueFrom(
              this._incomeService.initializeBorrowerIncomeAndEmploymentVerification(
                this.mainBorrower?.contactId,
                this.mortgageApplication.applicationId,
                'Argyle'
              )
            );
            if (response?.htmlWidgetSrc) {
              updateToken(this._argyleService.extractUserToken(response.htmlWidgetSrc));
            }
          } catch (error) {
            console.error('Failed to refresh Argyle token', error);
          }
        },
        onError: ({ errorType, errorMessage }: { errorType: string; errorMessage: string }) => {
          console.error('Argyle Link error:', errorType, errorMessage);
        }
      });
      this._argyleLink.open();
    } catch (error) {
      console.error('Failed to initialize Argyle Link', error);
    }
  };

  onNextClicked() {
    if (!this.voeEnabled) {
      this.wizardFlowService.navigateToManualEmploymentAndIncomeStep();
      return;
    }
    if (this.allFieldsFilled) {
      if (this.wizardFlowService.isEditEnabled) {
        super.onNextClicked();
        return;
      }
      if (this.step.vendorToUse === 'TWN') {
        this.startSpinner();
        const apiCalls: Observable<any>[] = [];
        for (const borrower of this.getBorrowersForDigitalIncomeCheck()) {
          const apiCall = this._incomeService.verifyIncomeAndEmploymentData(
            this.mortgageApplication.applicationId,
            borrower.contactId!
          );
          apiCalls.push(apiCall);
        }
        if (apiCalls.length) {
          forkJoin(apiCalls).subscribe(
            (results) => {
              for (const borrower of this.getBorrowersForDigitalIncomeCheck()) {
                const borrowerResult = results.find(
                  (r) => r.borrowerId === borrower.contactId
                );
                if (borrowerResult) {
                  this.extractIncomeAndEmploymentData(borrower, borrowerResult);
                }
              }
              this.stopSpinner();
              super.onNextClicked();
            },
            (error) => {
              this.stopSpinner();
              //this.alert = new Alert("An error occurred digitally verifying income/employment.", error.error.message, AlertType.Error);
              super.onNextClicked();
            }
          );
        } else {
          super.onNextClicked();
        }
      } else {
        super.onNextClicked();
      }
    } else {
      super.onNextClicked();
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    if (this.borrowerIncomeComponents) {
      for (const borrowerCredit of this.borrowerIncomeComponents) {
        borrowerCredit.checkStatusChanged.subscribe((e: any) =>
          this.onCheckClickedOnBorrowerCredit(e)
        );
      }
      this.allFieldsFilled = this.allBorrowersDigitalIncomeChecked();
      setTimeout(() => {
        this.setNextButtonText();
        this.cdr.detectChanges();
      });
    }
  }

  ngOnInit() {
    this.nextButtonText = this.translocoService.translate('common.skip');
    if (this.step.vendorToUse === 'Truv') {
      this.getTokenAndInitializeTruv();
    } else if (this.step.vendorToUse === 'Argyle') {
      this.getTokenAndInitializeArgyle();
    }
  }

  ngOnDestroy(): void {
    this._componentIsBeingDestroyed = true;
    this._timerExpiredSubscription?.unsubscribe();
    this._timerTickedSubscription?.unsubscribe();
    this._signalRVerificationSub?.unsubscribe();
    this._timerService.remove('process-events-timer');
    this._truvBridge?.close();
    this._argyleLink?.close();
  }
}
